// CreateAutomationDialog.js
// The one place an Automation is created: pick a Function, say when it should
// run, name it.
//
// Only a Function a person authored, published, and has not invalidated can be
// automated — a predefined Function is not the user's to schedule, and an
// unpublished one has nothing to run. The published version the picker read is
// carried into the write as `expectedVersion`, so a publish that lands between
// the pick and the Create button aborts the create instead of producing an
// automation pinned to a version that is already history.

import React, { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { formatText } from '../common/TextFormatterProvider';
import {
  buildDefaultTriggerParameters,
  buildTriggerDoc,
  createAutomation,
  newTriggerId,
} from '../../services/functionTriggers';
import {
  isFunctionRunnable,
  isPublishedVersion,
  loadRunTarget,
  versionDataSourceBlocks,
} from '../../services/functionVersions';
import { isExternalTable } from '../../utils/tableTypes';

function CreateAutomationDialog({
  open,
  onClose,
  companyName,
  functions = [],
  tables = [],
  actorUid,
  onCreated,
}) {
  const [functionId, setFunctionId] = useState('');
  const [target, setTarget] = useState(null);
  const [targetLoading, setTargetLoading] = useState(false);
  const [triggerType, setTriggerType] = useState('on_schedule');
  const [tableId, setTableId] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // A slower load for a Function the user has already moved off must not land
  // under the one now selected.
  const selectedFunctionRef = useRef(null);

  const candidates = (functions || []).filter((func) => func.author === 'user');

  const handlePickFunction = async (nextFunctionId) => {
    const func = candidates.find((candidate) => candidate.id === nextFunctionId);
    setFunctionId(nextFunctionId);
    selectedFunctionRef.current = nextFunctionId;
    setTarget(null);
    setTableId('');
    setName(func ? `${func.name || func.id} automation` : '');
    setError('');
    if (!companyName || !nextFunctionId) return;

    setTargetLoading(true);
    try {
      const loaded = await loadRunTarget(companyName, nextFunctionId);
      if (selectedFunctionRef.current !== nextFunctionId) return;
      setTarget(loaded);
    } catch (err) {
      if (selectedFunctionRef.current !== nextFunctionId) return;
      console.error('Failed to load the published version of this function:', err);
      setError('Failed to read this function. Close this dialog and try again.');
    } finally {
      if (selectedFunctionRef.current === nextFunctionId) setTargetLoading(false);
    }
  };

  // The tables an "on new data" automation may watch: the ones this version
  // reads, minus external tables, which never receive an upload.
  const dataSourceBlocks = versionDataSourceBlocks(target?.content);
  const readTableIds = new Set(
    Object.values(dataSourceBlocks).map((block) => block.table_id).filter(Boolean),
  );
  const watchableTables = (tables || []).filter(
    (table) => readTableIds.has(table.id) && !isExternalTable(table),
  );

  const missingTable = triggerType === 'on_new_data' && !tableId;
  const canCreate = Boolean(functionId)
    && Boolean(target?.content)
    && isPublishedVersion(target?.publishedVersion)
    && !missingTable
    && !targetLoading
    && !busy;

  const handleClose = () => {
    if (busy) return;
    setFunctionId('');
    selectedFunctionRef.current = null;
    setTarget(null);
    setTriggerType('on_schedule');
    setTableId('');
    setName('');
    setError('');
    onClose();
  };

  const handleCreate = async () => {
    if (!canCreate) return;
    const func = candidates.find((candidate) => candidate.id === functionId);
    setBusy(true);
    setError('');
    let created;
    try {
      created = await createAutomation(
        companyName,
        buildTriggerDoc({
          triggerId: newTriggerId(),
          functionId,
          functionName: func?.name || functionId,
          name,
          triggerType,
          tableId: triggerType === 'on_new_data' ? tableId : null,
          parameters: buildDefaultTriggerParameters(dataSourceBlocks),
          functionVersion: target.publishedVersion,
          actorUid,
        }),
        target.publishedVersion,
      );
    } catch (err) {
      console.error('Failed to create this automation:', err);
      setError(err.message);
      setBusy(false);
      return;
    }
    setBusy(false);
    handleClose();
    // Created and closed: what follows is the host's (refresh the list, select
    // the new automation) and reports its own failures on its own surface — an
    // error here must never be written into this dialog's now-hidden state.
    if (onCreated) await onCreated(created);
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>New Automation</DialogTitle>
      <DialogContent>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          An automation runs one of your functions unattended — on a schedule, or when new data
          lands in a table it reads.
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary">Function</Typography>
          <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
            <Select
              value={functionId}
              displayEmpty
              onChange={(e) => handlePickFunction(e.target.value)}
            >
              <MenuItem value="" disabled>Choose a function</MenuItem>
              {candidates.map((func) => {
                const runnable = isFunctionRunnable(func);
                return (
                  <MenuItem key={func.id} value={func.id} disabled={!runnable}>
                    {formatText(func.name || func.id)}
                    {!runnable && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        {func.invalid ? 'Invalid' : 'Publish first'}
                      </Typography>
                    )}
                  </MenuItem>
                );
              })}
              {candidates.length === 0 && (
                <MenuItem value="" disabled>No functions of your own yet</MenuItem>
              )}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary">When it runs</Typography>
          <RadioGroup
            value={triggerType}
            onChange={(e) => setTriggerType(e.target.value)}
          >
            <FormControlLabel
              value="on_schedule"
              control={<Radio size="small" />}
              label={<Typography variant="body2">On a schedule — daily at 09:00 to begin with, editable afterwards</Typography>}
            />
            <FormControlLabel
              value="on_new_data"
              control={<Radio size="small" />}
              label={<Typography variant="body2">When new data lands in a table this function reads</Typography>}
            />
          </RadioGroup>
        </Box>

        {triggerType === 'on_new_data' && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary">Watched table</Typography>
            <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
              <Select
                value={tableId}
                displayEmpty
                disabled={!functionId || targetLoading}
                onChange={(e) => setTableId(e.target.value)}
              >
                <MenuItem value="" disabled>Choose a table</MenuItem>
                {watchableTables.map((table) => (
                  <MenuItem key={table.id} value={table.id}>{formatText(table.name || table.id)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {functionId && !targetLoading && watchableTables.length === 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                This function reads no table that receives uploads, so it can only run on a schedule.
              </Typography>
            )}
          </Box>
        )}

        <TextField
          fullWidth
          size="small"
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={!functionId}
          inputProps={{ maxLength: 100 }}
        />

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={handleCreate} disabled={!canCreate}>
          {busy ? 'Creating…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CreateAutomationDialog;
